import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import IntegrationAuthProvider from '../../../IntegrationAuthProvider/IntegrationAuthProvider'
import Icon from '../../../../atoms/Icon'
import Loading from '../../../../molecules/Loading'
import FlexRow from '../../../../molecules/FlexRow'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import SydButton from '../../../../commonDesign/Button'
import useRedtailListPreset from './useRedtailListPreset'
import presets from './presets'
import MeetingsContent from './MeetingsContent'
import NotesContent from './NotesContent'
import WorkflowsContent from './WorkflowsContent'
import TasksContent from './TasksContent'

const SIZE_STYLES = {
  sm: { titleFontSize: '0.875rem', rowPadding: '8px 0', fontSize: 'var(--font-size-sm)', minHeight: '120px' },
  md: { titleFontSize: '1rem', rowPadding: '10px 0', fontSize: 'var(--font-size-sm)', minHeight: '180px' },
  lg: { titleFontSize: '1.25rem', rowPadding: '12px 0', fontSize: 'var(--font-size-md)', minHeight: '240px' }
}

function RedtailListInner ({ config, styleVariant }) {
  const presetKey = config?.preset
  const preset = presets[presetKey]
  const { data, items, members, isLoading, notLinked, title, icon } = useRedtailListPreset(presetKey, config)
  const [modalOpen, setModalOpen] = useState(false)

  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md
  const isNotesPreset = presetKey === 'latest-notes'
  const isWorkflowsPreset = presetKey === 'workflows'
  const isTasksPreset = presetKey === 'upcoming-tasks'

  const enableCreate = isNotesPreset
    ? true
    : (config?.enableCreate ?? preset?.defaults?.enableCreate ?? false)
  const enableEdit = config?.enableEdit ?? preset?.defaults?.enableEdit ?? false
  const formMode = config?.formMode ?? preset?.defaults?.formMode ?? 'calendar'

  const handleOpenModal = useCallback(() => setModalOpen(true), [])
  const handleCloseModal = useCallback(() => setModalOpen(false), [])

  if (!presetKey) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
        No preset configured for this list component.
      </div>
    )
  }

  if (isLoading) {
    return <Loading />
  }

  if (notLinked) {
    return (
      <FadeIn>
        <FlexRow className='justify-center items-center' style={{ minHeight: sizes.minHeight, color: 'var(--text-color-subtle)', fontSize: 'var(--font-size-xs)' }}>
          Client is not linked to Redtail
        </FlexRow>
      </FadeIn>
    )
  }

  const renderContent = () => {
    if (isNotesPreset) {
      return (
        <NotesContent
          data={data}
          members={members}
          config={config}
          createOpen={modalOpen}
          onCloseCreate={handleCloseModal}
        />
      )
    }
    if (isWorkflowsPreset) {
      return (
        <WorkflowsContent
          items={items}
          sizes={sizes}
          createOpen={modalOpen}
          onCloseCreate={handleCloseModal}
          config={config}
          members={members}
        />
      )
    }
    if (isTasksPreset) {
      return (
        <TasksContent
          items={items}
          sizes={sizes}
          members={members}
          config={config}
          enableCreate={enableCreate}
          enableEdit={enableEdit}
          formMode={formMode}
          createOpen={modalOpen}
          onCloseCreate={handleCloseModal}
        />
      )
    }
    return (
      <MeetingsContent
        items={items}
        sizes={sizes}
        members={members}
        config={config}
        createOpen={modalOpen}
        onCloseCreate={handleCloseModal}
        enableEdit={enableEdit}
        formMode={formMode}
      />
    )
  }

  return (
    <FadeIn>
      <div style={{ padding: '8px 12px' }}>
        <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {icon ? (
              <div style={{ display: 'flex', alignItems: 'center', fill: 'var(--color-primary-500)' }}>
                <Icon name={icon} customSize='24px' />
              </div>
            ) : null}
            <div style={{ fontSize: sizes.titleFontSize, fontWeight: 600 }}>
              {title}
            </div>
          </div>
          {enableCreate ? (
            <SydButton
              size='sm'
              variant='outline'
              icon='add'
              onClick={handleOpenModal}
            >
              Create
            </SydButton>
          ) : null}
        </FlexRow>

        {renderContent()}
      </div>
    </FadeIn>
  )
}

RedtailListInner.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string
}

function RedtailListRenderer ({ config, styleVariant, presentationKey, blueprint, displayName }) {
  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md

  return (
    <div style={{ minHeight: sizes.minHeight }}>
      <IntegrationAuthProvider integration='redtail' size='sm' loadingType='circular'>
        <RedtailListInner
          config={config}
          styleVariant={styleVariant}
          presentationKey={presentationKey}
          blueprint={blueprint}
          displayName={displayName}
        />
      </IntegrationAuthProvider>
    </div>
  )
}

RedtailListRenderer.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string,
  presentationKey: PropTypes.string,
  blueprint: PropTypes.object,
  displayName: PropTypes.string
}

export default RedtailListRenderer
